<template>
  <div class="comp-qf-table-body-group">
    <!-- 顶部填充 -->
    <div :style="{ height: paddingTop + 'px', zIndex: '-1' }"></div>
    <!-- 可见区域 -->
    <div class="qf-group-content" :style="{ height: totalHeight + 'px' }">
      <!-- 渲染可见数据 -->
      <div
        v-for="(item, index) in visibleData"
        :key="item._uniqueId || item.id"
        :data-index="index"
        :ref="(el) => el && measureItemHeight(item.$pos.index, el)"
        class="qf-group-table-virtual-item"
        :style="{
          top: `${item.$pos?.top ?? 0}px`
        }"
      >
        <!-- 一级分组顶部的8px占位 -->
        <div v-if="item.level === 1 && groupState[item.groupKey]" class="qf-table-group-extra-space"></div>
        <!-- 分组标题行 -->
        <div class="qf-table-group-title" v-if="shouldShowGroupTitle(item)">
          <div class="qf-table-group-title-text">
            <template v-for="i in item.level - 1" :key="i">
              <div class="qf-table-group-title-text-placeholder"></div>
            </template>
            <div class="expand-icon-container">
              <qfei-icon-caret-right-outlined @click.stop="toggleGroup(item, true)" v-if="!groupState[item.groupKey]" style="font-size: 10px" />
              <qfei-icon-caret-down-outlined @click.stop="toggleGroup(item, false)" v-else style="font-size: 10px" />
            </div>
            <slot name="group-table-title" :row="item"></slot>
          </div>
        </div>
        <!-- 数据行 -->
        <qf-table-row-group :ref="setRefs(item)" v-if="!item.level && !item.hasMore && !item.isEmpty" :row="item" :columns="columns" :totalTableWidth="totalTableWidth" :checkedParams="checkedParams" :errorRowList="errorRowList" :activeRowKeys="activeRowKeys" :rowIndex="item.rowIndex" :isGroup="groupNumber" :checkBoxClassName="checkBoxClassName" @rowClick="handleRowClick" @handleChecked="handleChecked" :cornerMark="cornerMark">
          <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </qf-table-row-group>

        <!-- 加载更多/空状态 -->
        <div class="qf-table-group-title load-more-group-container" v-if="!item.level && (item.hasMore || item.isEmpty)">
          <div class="qf-table-group-title-text">
            <div class="qf-table-group-title-text-placeholder" v-for="n in groupNumber - 1" :key="n"></div>
            <!-- <div class="corner-mark" v-if="cornerMark"></div> -->
            <div :style="{ marginLeft: `${qfTableWidth / 2 - 70}px` }">
              <template v-if="item.hasMore">
                <slot name="load-more-group-data" :loadMoreInfo="item"></slot>
              </template>
              <div v-if="item?.isEmpty" class="group-empty">{{ item.textPlaceholder || '暂无数据' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 底部填充 -->
    <div :style="{ height: paddingBottom + 'px', zIndex: '-1' }"></div>
  </div>
</template>

<script setup>
import { ref, computed, inject, nextTick, reactive } from 'vue';
import { QfeiIconCaretRightOutlined, QfeiIconCaretDownOutlined } from '@qfei-design/icons-vue';
import QfTableRowGroup from '../qf-table-row-group/qf-table-row-group.vue';
import { useVirtualScroll } from '../hooks/groupVirtualList';

const props = defineProps({
  columns: { type: Array, default: () => [] },
  groupData: { type: Array, default: () => [] },
  rowKey: { type: String, default: 'id' },
  errorRowList: { type: Array, default: () => [] },
  activeRowKeys: { type: Array, default: () => [] },
  totalTableWidth: { type: Number, default: 0 },
  groupInfo: { type: Array, default: () => [] },
  cornerMark: { type: Boolean, default: false },
  checkedParams: {
    type: Object,
    default: () => ({
      enabled: true,
      type: 'checkbox',
      width: 40
    })
  }
});
// 使用对象存储所有行组件的 ref
const tableRowRefs = reactive({});
// 获取行组件 ref 的函数
const setRefs = (row) => {
  return (el) => {
    tableRowRefs[`tableRow_${row[props.rowKey]}`] = el;
  };
};
const emits = defineEmits(['rowClick', 'handleChecked', 'loadGroupData']);
const groupNumber = computed(() => props.groupInfo?.length);
const tableData = computed(() => props.groupData);
const groupState = ref(inject('groupState')?.groupState); //组组件已经处理好分组状态，这里直接使用，结构是{groupKey: true}
// 虚拟列表滚动过程中检查视口范围内哪些数据没有加载过需要用到createGroupMap，由于初始化就会循环数据源，所以提前生成需要使用的MAP对象，这里可以直接使用
const createGroupMap = inject('groupState')?.createGroupMap.value;
const qfTableHeaderBodyRef = inject('qfTable')?.qfTableHeaderBodyRef;
const qfTableWidth = ref(inject('qfTable')?.qfTableWidth);

// 使用虚拟滚动Hook
const { visibleData, paddingTop, paddingBottom, totalHeight, measureItemHeight, recalculate, updateGroupState, onGroupEnterViewport } = useVirtualScroll(tableData, qfTableHeaderBodyRef, groupState, groupNumber.value, createGroupMap);

// 辅助方法：判断是否应该显示分组标题
const shouldShowGroupTitle = (item) => {
  if (!item.level) return false;
  // 一级分组始终显示
  if (item.level === 1) return true;
  // 二级分组仅在其父分组展开时显示
  if (item.level === 2) return groupState.value[item.gk1];
  // 三级分组仅在其祖父和父分组都展开时显示
  if (item.level === 3) return groupState.value[item.gk1] && groupState.value[item.gk2];
  return false;
};

// 切换分组展开/折叠状态 - 确保状态正确传播
const toggleGroup = (item, isExpanded) => {
  // 立即更新状态
  updateGroupState(item.groupKey, isExpanded);
  nextTick(() => {
    recalculate();
  });
  // 触发父组件事件加载数据
  emits('loadGroupData', {
    groupKey: item.groupKey,
    dataIds: item.dataIds,
    level: item.level
  });
};

// 设置分组进入视口的回调 - 确保折叠后新进入视口的数据能触发加载
onGroupEnterViewport.value = ({ groupKey, dataIds, level }) => {
  // 检查分组是否处于展开状态，只有展开状态才加载数据
  if (groupState.value[groupKey]) {
    emits('loadGroupData', { groupKey, dataIds, level });
  }
};
// 提供修改行数据的z-index方法
const updateZIndex = ({ rowKey, zIndex }) => {
  const rowRef = tableRowRefs[`tableRow_${rowKey}`];
  if (rowRef) {
    rowRef.updateZIndex(zIndex);
  }
};
// 处理行点击事件
const handleRowClick = (row) => {
  emits('rowClick', row);
};

// 处理选中事件
const handleChecked = (row) => {
  emits('handleChecked', row);
};

// 暴露recalculate方法供父组件调用
defineExpose({ recalculate, updateZIndex });
</script>

<style scoped lang="less">
@import './qf-table-body-group.less';
</style>
